import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { format, parseISO } from "date-fns";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { StandardizedItem } from "@/types/dashboard";

const API_URL = import.meta.env.VITE_API_URL;

// Interface matching the exact API response structure
interface SystemStatusApiResponse {
  id: number;
  name: string;
  details: string;
  status: string;
  lastUpdated: string;
}

// Fallback data in case API fails
const fallbackSystemStatus: SystemStatusApiResponse[] = [
  {
    id: 1,
    name: "Dove Bot System",
    details: "API connection to Claude is functioning normally",
    status: "operational",
    lastUpdated: "2023-01-15T12:00:00-08:00"
  },
  {
    id: 2,
    name: "Dove Bot System",
    details: "Self-reflection process running on schedule",
    status: "operational",
    lastUpdated: "2023-03-10T12:00:00-08:00"
  },
  {
    id: 3,
    name: "Dove Bot System",
    details: "Chat functionality is working properly",
    status: "operational",
    lastUpdated: "2023-05-05T12:00:00-08:00"
  }
];

// Helper function to safely format dates
const formatDate = (dateString: string): string => {
  try {
    // Parse the ISO string to a Date object
    const date = parseISO(dateString);
    return format(date, 'PPP');
  } catch (error) {
    console.error("Error formatting date:", error, "Date string:", dateString);
    return dateString || 'N/A';
  }
};

// Function to transform API response to standardized format
const transformToStandardized = (data: SystemStatusApiResponse[]): StandardizedItem[] => {
  // Get run_cycle from local storage or use default
  const latestRunCycle = localStorage.getItem('runCycle') ? 
    parseInt(localStorage.getItem('runCycle') || '125') : 125;
  
  return data.map(item => ({
    id: item.id,
    date: item.lastUpdated,
    run_cycle: latestRunCycle,
    text: item.details
  }));
};

export const SystemStatus = () => {
  // Get runCycle from API
  useQuery({
    queryKey: ['runCycle'],
    queryFn: async () => {
      try {
        const response = await axios.get(`${API_URL}/run_cycle`);
        if (response.data && response.data.completed) {
          localStorage.setItem('runCycle', response.data.completed.toString());
        }
        return response.data;
      } catch (error) {
        console.error("Error fetching run cycle:", error);
        return null;
      }
    }
  });

  // Fetch system status data
  const {
    data: systemStatusData,
    isLoading,
    error
  } = useQuery({
    queryKey: ['systemStatus'],
    queryFn: async () => {
      try {
        const response = await axios.get<SystemStatusApiResponse[]>(`${API_URL}/system-status`);
        console.log('System status API response:', response.data);
        return transformToStandardized(response.data);
      } catch (error) {
        console.error("Error fetching system status:", error);
        return transformToStandardized(fallbackSystemStatus);
      }
    }
  });

  return (
    <Card className="w-full max-w-4xl mx-auto bg-white/80 backdrop-blur-sm shadow-lg">
      <CardHeader>
        <CardTitle className="text-3xl font-display text-center text-primary">System Status</CardTitle>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-1/6">Date</TableHead>
              <TableHead className="w-1/6">Run Cycle</TableHead>
              <TableHead className="w-4/6">Text</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading && (
              <TableRow>
                <TableCell colSpan={3} className="text-center py-4">Loading system status...</TableCell>
              </TableRow>
            )}
            {error && (
              <TableRow>
                <TableCell colSpan={3} className="text-center py-4 text-red-500">Error loading system status</TableCell>
              </TableRow>
            )}
            {!isLoading && !error && (systemStatusData || transformToStandardized(fallbackSystemStatus)).map((item, index) => (
              <TableRow 
                key={item.id}
                className="fade-in"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <TableCell className="text-sm text-muted-foreground">
                  {formatDate(item.date)}
                </TableCell>
                <TableCell>{item.run_cycle}</TableCell>
                <TableCell className="font-medium">{item.text}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
};
